/**
 * Vocabulário e helpers da escala de itinerantes, compartilhados pela tela
 * do admin, pela tela do itinerante e pela versão para impressão.
 */

export const TURNOS = [
  { key: 'manha', label: 'Manhã', curto: 'MANHÃ' },
  { key: 'tarde', label: 'Tarde', curto: 'TARDE' },
  { key: 'noite', label: 'Noite', curto: 'NOITE' },
];

export const TURNO_LABEL = { manha: 'Manhã', tarde: 'Tarde', noite: 'Noite' };
export const CAMPO_VAGAS = { manha: 'vagasManha', tarde: 'vagasTarde', noite: 'vagasNoite' };

export const STATUS_LABEL = {
  pendente: 'Aguardando',
  confirmado: 'Confirmado',
  recusado: 'Não selecionado',
  cancelado: 'Cancelado',
};

export const MODO_LABEL = {
  ordem_chegada: 'Ordem de chegada',
  escolha_admin: 'Escolha do administrador',
};

export const MODO_DESC = {
  ordem_chegada:
    'Quem pedir primeiro fica com a vaga: a inscrição é confirmada na hora enquanto houver vaga no turno. Os demais entram numa fila e sobem sozinhos quando alguém desiste ou você abre mais vagas.',
  escolha_admin:
    'Os pedidos ficam aguardando e você escolhe quem vai em cada turno. Nada é confirmado sem a sua decisão.',
};

export const CICLO_STATUS_LABEL = {
  rascunho: 'Rascunho',
  publicado: 'Publicado',
  encerrado: 'Encerrado',
};

export const CICLO_STATUS_DESC = {
  rascunho: 'Só você vê. Monte os dias, as vagas e os feriados com calma.',
  publicado: 'Os itinerantes veem o calendário e pedem vagas.',
  encerrado: 'Escala fechada: ninguém mais pede vaga nem desiste.',
};

export const DIAS_SEMANA = [
  'Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado',
];
export const DIAS_SEMANA_CURTO = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// Mesmos valores do enum MilitaryRank do backend.
const POSTO_ABREV = {
  'Coronel': 'CEL',
  'Tenente-Coronel': 'TC',
  'Major': 'MAJ',
  'Capitão': 'CAP',
  '1º Tenente': '1º TEN',
  '2º Tenente': '2º TEN',
  'Subtenente': 'ST',
  '1º Sargento': '1º SGT',
  '2º Sargento': '2º SGT',
  '3º Sargento': '3º SGT',
  'Cabo': 'CB',
  'Soldado': 'SD',
};

export const postoAbrev = (posto) => POSTO_ABREV[posto] || posto || '';

const PARTICULAS = new Set(['DE', 'DA', 'DO', 'DAS', 'DOS', 'E']);

/** "FRANCISCO JOSÉ DOS SANTOS" → "FRANCISCO SANTOS" (cabe no calendário). */
export const nomeCurto = (nome) => {
  const partes = String(nome || '')
    .trim()
    .split(/\s+/)
    .filter((p) => p && !PARTICULAS.has(p.toUpperCase()));
  if (partes.length <= 2) return partes.join(' ');
  return `${partes[0]} ${partes[partes.length - 1]}`;
};

/** Como o nome aparece na escala: "2º SGT FRANCISCO SANTOS". */
export const nomeEscala = (pessoa, completo = false) =>
  [postoAbrev(pessoa?.posto), completo ? pessoa?.nome : nomeCurto(pessoa?.nome)]
    .filter(Boolean)
    .join(' ');

export const formatDataBR = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '');
export const formatDataCurta = (iso) => (iso ? formatDataBR(iso).slice(0, 5) : '');

export const parseIsoLocal = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const toIsoLocal = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const addDias = (iso, n) => {
  const d = parseIsoLocal(iso);
  d.setDate(d.getDate() + n);
  return toIsoLocal(d);
};

export const diaSemanaDe = (iso) => parseIsoLocal(iso).getDay();

export const periodoLabel = (ciclo) =>
  ciclo ? `${formatDataBR(ciclo.dataInicio)} a ${formatDataBR(ciclo.dataFim)}` : '';

export const formatDataHora = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Ciclo de pagamento (16 → 15) que contém a data. Sugestão para o admin ao
 * criar um ciclo novo: o corrente, ou o seguinte se o corrente já existe.
 */
export const cicloContendo = (iso) => {
  const d = parseIsoLocal(iso);
  const y = d.getFullYear();
  const m = d.getMonth();
  if (d.getDate() >= 16) {
    return { dataInicio: toIsoLocal(new Date(y, m, 16)), dataFim: toIsoLocal(new Date(y, m + 1, 15)) };
  }
  return { dataInicio: toIsoLocal(new Date(y, m - 1, 16)), dataFim: toIsoLocal(new Date(y, m, 15)) };
};

export const sugerirNovoCiclo = (ciclosExistentes = []) => {
  let sugestao = cicloContendo(toIsoLocal(new Date()));
  const existe = (s) => ciclosExistentes.some((c) => c.dataInicio === s.dataInicio && c.dataFim === s.dataFim);
  let tentativas = 0;
  while (existe(sugestao) && tentativas < 12) {
    sugestao = cicloContendo(addDias(sugestao.dataFim, 1));
    tentativas += 1;
  }
  return sugestao;
};

export const nomeCicloSugerido = (dataInicio, dataFim) =>
  dataInicio && dataFim ? `Ciclo ${formatDataCurta(dataInicio)} a ${formatDataCurta(dataFim)}` : '';

/** Turnos que existem no ciclo (noite só aparece se algum dia tiver vaga). */
export const turnosDoCiclo = (dias = []) =>
  TURNOS.filter(
    (t) => t.key !== 'noite' || dias.some((d) => (d[CAMPO_VAGAS.noite] || 0) > 0),
  );

/** Opções de dias da semana do ciclo (mapeadas em incluirSabado/incluirDomingo). */
export const DIAS_OPCOES = [
  { key: 'uteis', label: 'Segunda a sexta', desc: 'Sábado e domingo nascem sem vagas.', incluirSabado: false, incluirDomingo: false },
  { key: 'seg_sab', label: 'Segunda a sábado', desc: 'Só o domingo fica de fora.', incluirSabado: true, incluirDomingo: false },
  { key: 'todos', label: 'Todos os dias', desc: 'Sábado e domingo também têm vagas.', incluirSabado: true, incluirDomingo: true },
];

export const diasOpcaoDe = (ciclo) => {
  if (ciclo?.incluirSabado && ciclo?.incluirDomingo) return 'todos';
  if (ciclo?.incluirSabado) return 'seg_sab';
  return 'uteis';
};

export const DIAS_OPCAO_LABEL = Object.fromEntries(DIAS_OPCOES.map((o) => [o.key, o.label]));

const diaTemAlgo = (d) => d.feriado || d.vagasManha + d.vagasTarde + d.vagasNoite > 0;

/**
 * Quais colunas de fim de semana a grade mostra sozinha: sábado se algum
 * sábado tem vaga (ou feriado), idem domingo. "Segunda a sábado" fica com
 * 6 colunas, sem um domingo vazio sobrando.
 */
export const fimDeSemanaComVaga = (dias = []) => ({
  sabado: dias.some((d) => d.diaSemana === 6 && diaTemAlgo(d)),
  domingo: dias.some((d) => d.diaSemana === 0 && diaTemAlgo(d)),
});

/**
 * Agrupa os dias em semanas (segunda → domingo). Cada semana tem 5, 6 ou 7
 * células; célula null = fora do período (ou coluna escondida).
 * `mostrar` = { sabado, domingo }.
 */
export const agruparSemanas = (dias = [], mostrar = { sabado: false, domingo: false }) => {
  const colunas = [1, 2, 3, 4, 5];
  if (mostrar.sabado) colunas.push(6);
  if (mostrar.domingo) colunas.push(0);
  const visivel = new Set(colunas);
  const porData = new Map(dias.map((d) => [d.data, d]));
  const semanas = new Map();
  for (const dia of dias) {
    const w = diaSemanaDe(dia.data);
    if (!visivel.has(w)) continue;
    const offset = (w + 6) % 7; // segunda = 0 … domingo = 6
    const segunda = addDias(dia.data, -offset);
    if (!semanas.has(segunda)) {
      semanas.set(segunda, {
        chave: segunda,
        celulas: colunas.map((c) => {
          const off = (c + 6) % 7;
          const data = addDias(segunda, off);
          return porData.get(data) || null;
        }),
      });
    }
  }
  return { colunas, semanas: [...semanas.values()].sort((a, b) => (a.chave < b.chave ? -1 : 1)) };
};

export const mensagemErro = (error, padrao = 'Algo deu errado.') => {
  const m = error?.response?.data?.message;
  if (Array.isArray(m)) return m.join(' ');
  return m || error?.message || padrao;
};
